import { Injectable } from '@angular/core';
import gql from 'graphql-tag';
import {
  Channel,
  GetCommunicationChannelForStickQuery,
  OnDevicesAPISubscription,
  SendModbusCommandForLoggerEthPollingMutation,
} from 'ngx-core-services';
import {
  fromHexStringToUint8Array,
} from 'ngx-modbus';
import { filter, first } from 'rxjs/operators';
import { ViewportScroller } from '@angular/common';
import { Router } from '@angular/router';
import { ToastService, ErrorSinkService } from 'ngx-zcs-components';

const ETH_DEBUG = { ENABLED: true };
const ethLog = (msg: string) => ETH_DEBUG.ENABLED && console.log(`[Modbus:EthZCS] ${msg}`);

import { SolarmanService } from './solarman.service';
import { BaseZCSChannelService } from './base-zcs-channel.service';
import { blockServiceLabels } from '../block-translations.service';


@Injectable({
  providedIn: 'root',
})
export class InternetEthZCSService extends BaseZCSChannelService {

  readonly refreshByDefault = 20 * 60 * 1000;
  protected readonly maxTimeoutInMs = 30 * 1000;
  protected readonly modbusAddress = 1;
  protected readonly delayBetweenCommandsInMs = 2000;

  constructor(
    getCommunicationChannelForStickQuery: GetCommunicationChannelForStickQuery,
    onDevicesAPISubscription: OnDevicesAPISubscription,
    router: Router,
    private readonly sendModbusCommandForLoggerEthPollingMutation: SendModbusCommandForLoggerEthPollingMutation,
    solarmanService: SolarmanService,
    toastService: ToastService,
    viewportScroller: ViewportScroller,
    errorSink: ErrorSinkService,
  ) {
    super(
      getCommunicationChannelForStickQuery,
      onDevicesAPISubscription,
      router,
      solarmanService,
      toastService,
      viewportScroller,
      errorSink,
    );

    this.interaction = this.interaction.bind(this);
  }

  protected validateChannel(data: { CommunicationChannel: Channel; LoggerStatus: string }): void {
    const isEth = data.CommunicationChannel === Channel.Azzurro_Ethernet;
    const isOnline = data.LoggerStatus === 'connected';

    if (!isEth || !isOnline) {
      throw new Error(blockServiceLabels.ethChannelConnectionError);
    }
  }

  async interaction(cmd: Uint8Array): Promise<Uint8Array> {
    if (!this.deviceId) {
      throw new Error(blockServiceLabels.deviceNotFoundError || 'Device not found');
    }

    const content = Array.from(cmd).map(x => x.toString(16).padStart(2, '0')).join('');
    const loggerSn = this.deviceParams?.communication.parentSn;

    ethLog(`SEND - ${content}`);

    const mqttFragment = gql`
      fragment sendcommandstringresponse on SendCommandStringResponse {
        collectionTime
        orderId
        success
        code
        msg
        connectStatus
      }
    `;

    const zcsRequest = async () =>
      (await this.sendModbusCommandForLoggerEthPollingMutation.execute(mqttFragment, {
        loggerId: loggerSn!,
        deviceSN: this.deviceId!,
        content: [content],
      })).data?.sendModbusCommandForLoggerEthPolling;

    const request = await zcsRequest();

    if (request?.code && request.code !== '500') {
      throw new Error(blockServiceLabels.requestFailedError || 'Request failed');
    }

    const response = await this.responseByAppsync$.pipe(
      filter(r => r.orderId === Number(request?.orderId)),
      first(),
    ).toPromise();

    const responseAsUint = fromHexStringToUint8Array(response.ack);
    ethLog(`RECEIVE - 0x${Array.from(responseAsUint).map(x => x.toString(16).padStart(2, '0')).join(' ')}`);
    this.response$.next(responseAsUint);
    return responseAsUint;
  }
}
